import { Check, CircleSlash, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Item,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from "@opencloud/ui";
import type { GateEvidence, ReviewEvidence } from "../types.js";
import { releaseCheckSummary } from "../presentation.js";

export function Evidence({
  reviews,
  gates,
  record = false,
}: {
  reviews: readonly ReviewEvidence[];
  gates: readonly GateEvidence[];
  record?: boolean;
}) {
  const summary = releaseCheckSummary(reviews, gates);
  return (
    <div className="a3-checks" data-record={record}>
      <div className="a3-checks-summary" data-tone={summary.tone} role="status">
        <span className="a3-checks-summary-icon" aria-hidden="true">
          {summary.tone === "passed" ? (
            <Check />
          ) : summary.tone === "attention" ? (
            <X />
          ) : (
            <CircleSlash />
          )}
        </span>
        <span>
          <strong>{summary.label}</strong>
          <small>{summary.detail}</small>
        </span>
      </div>
      {reviews.length > 0 ? (
        <Accordion className="a3-reviews" aria-label="Independent reviews">
          {reviews.map((review) => (
            <ReviewRow key={`${review.area}:${review.label}`} review={review} />
          ))}
        </Accordion>
      ) : null}
      {gates.length > 0 ? (
        <div className="a3-gates" aria-label="Verification checks">
          <h4>{record ? "Checks on this release" : "Checks"}</h4>
          <ul>
            {gates.map((gate) => (
              <Item
                key={gate.id}
                render={<li />}
                size="xs"
                variant="default"
                className="a3-gate-check"
                data-status={gate.status}
              >
                <ItemMedia
                  variant="icon"
                  className="a3-gate-check-icon"
                >
                  <GateIcon status={gate.status} />
                </ItemMedia>
                <ItemContent className="a3-gate-check-copy">
                  <ItemTitle className="a3-gate-check-title">
                    {gate.label}
                  </ItemTitle>
                  <small className="a3-gate-check-detail">{gate.detail}</small>
                </ItemContent>
              </Item>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ReviewRow({ review }: { review: ReviewEvidence }) {
  return (
    <AccordionItem
      className="a3-review"
      data-outcome={review.status}
      value={`${review.area}:${review.label}`}
    >
      <AccordionTrigger className="a3-review-head">
        <span className="a3-review-title">{review.label}</span>
        <Badge
          className="a3-review-outcome"
          tone={
            review.status === "passed"
              ? "success"
              : review.status === "not_needed"
                ? "neutral"
                : "warning"
          }
        >
          {review.status === "passed"
            ? "Passed"
            : review.status === "not_needed"
              ? "Not needed"
              : "Needs work"}
        </Badge>
      </AccordionTrigger>
      <AccordionContent>
        <p className="a3-review-detail">{review.detail}</p>
      </AccordionContent>
    </AccordionItem>
  );
}

function GateIcon({ status }: { status: GateEvidence["status"] }) {
  if (status === "passed") return <Check aria-hidden="true" />;
  if (status === "not_applicable") return <CircleSlash aria-hidden="true" />;
  if (status === "not_tested") return <CircleSlash aria-hidden="true" />;
  return <X aria-hidden="true" />;
}
